import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { sendReceipt } from "./store";
import { RECEIPT_LABEL, RECEIPT_TONE, fmtTime, rp } from "./util";

export function InvoiceView({ txn }) {
    return (
        <div className="bg-white text-ink" data-testid="pos-invoice">
            <div className="flex flex-col items-center border-b border-dashed border-mist pb-3">
                <img src="/pos-logo.webp" alt="Hoshizora" className="h-12 w-auto object-contain [mix-blend-mode:multiply]" data-testid="pos-invoice-logo" />
                <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-neutral-500">Invoice</p>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 py-3 text-xs border-b border-dashed border-mist">
                <span className="text-neutral-500">Transaction No.</span><span className="text-right font-mono" data-testid="pos-invoice-number">{txn.number}</span>
                <span className="text-neutral-500">Date & Time</span><span className="text-right">{fmtTime(txn.created_at)}</span>
                <span className="text-neutral-500">Customer</span><span className="text-right" data-testid="pos-invoice-customer">{txn.customer_name}</span>
                <span className="text-neutral-500">WhatsApp</span><span className="text-right">{txn.customer_wa}</span>
            </div>
            <div className="divide-y divide-mist/70 py-1">
                {txn.items.map((x) => (
                    <div key={x.item_id} className="py-2 text-xs" data-testid={`pos-invoice-line-${x.item_id}`}>
                        <p className="text-sm text-ink leading-snug">{x.name}{x.variant ? ` · ${x.variant}` : ""}</p>
                        <div className="mt-0.5 flex justify-between gap-2 text-neutral-500">
                            <span className="truncate">{x.sku} · {x.qty} × {rp(x.price)}</span>
                            <span className="shrink-0 text-ink">{rp(x.line_total)}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="border-t border-dashed border-mist pt-3 space-y-1">
                <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-widest text-neutral-500">Grand Total</span>
                    <span className="text-xl font-semibold" data-testid="pos-invoice-total">{rp(txn.total)}</span>
                </div>
                <div className="flex justify-between text-xs"><span className="text-neutral-500">Payment Method</span><span data-testid="pos-invoice-payment">{txn.payment_method}</span></div>
            </div>
            <p className="mt-4 text-center text-sm font-serif text-sage-dark" data-testid="pos-invoice-footer">Thank you for shopping with Hoshizora ✨</p>
        </div>
    );
}

export function SendWhatsAppButton({ txn, reload, className = "" }) {
    const [busy, setBusy] = useState(false);
    const st = txn.receipt?.status || "PENDING";
    if (st === "SENT") return <p className={`text-xs text-sage-dark ${className}`} data-testid="pos-wa-sent">WhatsApp sent · {fmtTime(txn.receipt.sent_at)}</p>;
    const send = async () => {
        setBusy(true);
        try {
            const r = await sendReceipt(txn.id);
            if (r.status === "SENT") toast.success("WhatsApp receipt sent");
            else toast.message("Receipt queued · stays Pending until online");
        } catch (e) {
            toast.error(e.message || "WhatsApp send failed");
        } finally {
            await reload();
            setBusy(false);
        }
    };
    return (
        <button onClick={send} disabled={busy || st === "SENDING"} data-testid="pos-wa-send"
            className={`h-12 rounded-md border border-sage-dark px-2 text-sage-dark text-xs font-semibold uppercase tracking-wider hover:bg-sage-dark hover:text-white transition-colors disabled:opacity-50 ${className}`}>
            {busy || st === "SENDING" ? "Sending…" : st === "FAILED" ? "Resend WhatsApp" : "Send WhatsApp"}
        </button>
    );
}

export function ReceiptStatus({ txn }) {
    const st = txn.receipt?.status || "PENDING";
    return (
        <p className="text-[11px] text-neutral-500" data-testid="pos-receipt-status">
            WhatsApp receipt: <span className={RECEIPT_TONE[st]}>{RECEIPT_LABEL[st]}</span>
            {txn.receipt?.last_error && st !== "SENT" ? ` · ${txn.receipt.last_error}` : ""}
        </p>
    );
}

export default function InvoiceDialog({ txn, onClose, reload }) {
    return (
        <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm p-5 max-h-[92vh] overflow-y-auto" data-testid="pos-invoice-dialog">
                <DialogTitle className="sr-only">Invoice</DialogTitle>
                {txn && (
                    <div className="space-y-3">
                        <InvoiceView txn={txn} />
                        <ReceiptStatus txn={txn} />
                        <div className="grid grid-cols-2 gap-2">
                            <SendWhatsAppButton txn={txn} reload={reload} className="w-full" />
                            <button onClick={onClose} data-testid="pos-invoice-done" className="h-12 rounded-md bg-sage-dark text-white text-xs font-semibold uppercase tracking-wider hover:opacity-90">Done</button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
